import { apiFetch } from '@/lib/api-client';

/**
 * Response shape from `POST /api/gifts/redeem` (backend block 2
 * contract at src/gifts/services/gift-redemption.service.ts).
 */
export interface RedeemGiftResponse {
  balanceCentsAfter: number;
  addedCents: number;
  giftProductKey: string;
  giftProductName: string;
}

/**
 * Response shape from `GET /api/gifts/balance` (backend block 4
 * contract at src/gifts/gifts.controller.ts).
 */
export interface GiftBalanceResponse {
  balanceCents: number;
}

/**
 * Redeem a gift code. Backend does the normalisation (uppercase,
 * dashes re-inserted, junk stripped) — the PWA passes raw input.
 * Errors surface as `ApiError` with envelope.user_message set to the
 * verbatim catalog copy: `gift_code_not_found`, `gift_code_already_redeemed`,
 * `gift_code_expired`, `gift_code_refunded`, `gift_redeem_rate_limited`.
 * The redeem page renders `user_message` directly per the PWA rule.
 */
export async function redeemGiftCode(rawCode: string): Promise<RedeemGiftResponse> {
  return apiFetch<RedeemGiftResponse>('/api/gifts/redeem', {
    method: 'POST',
    body: { code: rawCode },
  });
}

/**
 * Read the current euro-cent balance for the authenticated user.
 * Silent-friendly: if the request fails (feature flag off, backend
 * down, envelope malformed) the caller renders nothing. See the
 * `useGiftBalance` hook below for the swallow-and-return-null shape.
 */
export async function fetchGiftBalance(): Promise<GiftBalanceResponse> {
  return apiFetch<GiftBalanceResponse>('/api/gifts/balance', {
    method: 'GET',
  });
}

/**
 * Response shape from `POST /api/gifts/redeem-for-book-review`.
 * Mirrors backend `redeemForBookReview` return type.
 *
 * - `redeemed`           — code was fresh and has been credited now.
 * - `already_on_account` — same user re-entered their own already-
 *                          redeemed code; the balance is already
 *                          correct (soft success, no error).
 */
export type RedeemForBookReviewResponse =
  | {
      outcome: 'redeemed';
      balanceCentsAfter: number;
      addedCents: number;
      giftProductName: string;
    }
  | {
      outcome: 'already_on_account';
      balanceCentsAfter: number;
    };

/**
 * Redeem a gift code from the book review screen. Same validation
 * rules + rate-limit as `/redeem`, but a same-user re-entry returns
 * `outcome: 'already_on_account'` instead of throwing
 * `gift_code_already_redeemed`. All other errors (unknown / expired /
 * belongs-to-other-account / rate-limited / format-fail) are identical
 * to `/redeem` and surface as `ApiError` with the catalog user_message.
 */
export async function redeemGiftCodeForBookReview(
  rawCode: string,
): Promise<RedeemForBookReviewResponse> {
  return apiFetch<RedeemForBookReviewResponse>('/api/gifts/redeem-for-book-review', {
    method: 'POST',
    body: { code: rawCode },
  });
}
